import { pokerCardsOnly } from './cards.js';

/** @typedef {import('./cards.js').Card} Card */

const NUM_SEATS = 6;

/** @param {Array<{ betThisRound: number, folded: boolean }>} players */
export function maxBetRound(players) {
  const active = players.filter((p) => !p.folded);
  if (active.length === 0) return 0;
  return Math.max(...active.map((p) => p.betThisRound));
}

/**
 * Chips in the middle this street (approximation for sizing).
 */
export function potThisRound(players) {
  return players.reduce((s, p) => s + p.betThisRound, 0);
}

export function countActivePlayers(players) {
  return players.filter((p) => !p.folded).length;
}

export function potOdds(toCall, pot) {
  if (toCall <= 0) return 0;
  return toCall / (pot + toCall + 1e-6);
}

/**
 * @typedef {'premium'|'strong'|'playable'|'marginal'|'weak'} PreflopBucket
 * Spec §4 pre-flop
 */
export function classifyPreflop(hole) {
  const h = pokerCardsOnly(hole || []);
  if (h.length < 2) return 'weak';
  const [a, b] = h[0].rank <= h[1].rank ? [h[0], h[1]] : [h[1], h[0]];
  const hi = b.rank;
  const lo = a.rank;
  const suited = a.suit === b.suit;

  if (hi === lo) {
    if (hi >= 12) return 'premium';
    if (hi >= 10) return 'strong';
    if (hi >= 7) return 'playable';
    return 'marginal';
  }

  if (hi === 14 && lo === 13) return suited ? 'premium' : 'strong';
  if (hi === 14 && lo === 12) return suited ? 'strong' : 'playable';
  if (hi === 14 && lo === 11) return suited ? 'strong' : 'marginal';
  if (hi === 14 && lo === 10) return suited ? 'playable' : 'marginal';
  if (hi === 13 && lo === 12) return suited ? 'strong' : 'playable';
  if (hi === 13 && lo === 11) return suited ? 'playable' : 'marginal';
  if (hi === 12 && lo === 11) return suited ? 'playable' : 'marginal';
  if (hi === 11 && lo === 10) return suited ? 'playable' : 'marginal';
  if (hi === 10 && lo === 9 && suited) return 'playable';
  if (hi === 14 && lo === 12 && !suited) return 'playable';

  if (suited && hi === 14 && lo <= 9 && lo >= 2) return 'marginal';
  if (suited && hi - lo === 1 && hi >= 8 && lo >= 5) return 'marginal';
  if (!suited && hi >= 12 && lo >= 10) return 'marginal';

  return 'weak';
}

/**
 * @typedef {'monster'|'very_strong'|'strong'|'medium'|'weak_showdown'|'air'} MadeBucket
 */
const CAT_HIGH = 1;
const CAT_PAIR = 2;
const CAT_TWO_PAIR = 3;
const CAT_TRIPS = 4;
const CAT_STRAIGHT = 5;
const CAT_FLUSH = 6;
const CAT_FULL = 7;
const CAT_QUADS = 8;
const CAT_STR_FLUSH = 9;

/**
 * @param {Card[]} hole
 * @param {Card[]} board
 * @param {{ category: number, ranks: number[] }} ev best 7
 * @param {number} boardMaxRank
 */
export function classifyPostflopMade(hole, board, ev, boardMaxRank) {
  const b = pokerCardsOnly(board || []);
  if (!b.length) return 'air';

  if (ev.category >= CAT_FULL) return 'monster';
  if (ev.category === CAT_QUADS) return 'monster';
  if (ev.category === CAT_STR_FLUSH) return 'monster';

  if (ev.category === CAT_FLUSH || ev.category === CAT_STRAIGHT) {
    const top = ev.ranks[0] || 0;
    if (top >= 12 || ev.category === CAT_FLUSH) return 'very_strong';
    return 'strong';
  }

  if (ev.category === CAT_TRIPS) {
    if (b.length >= 3 && b.some((c, i, arr) => arr.findIndex((x) => x.rank === c.rank) !== i)) {
      return 'monster';
    }
    return 'very_strong';
  }

  if (ev.category === CAT_TWO_PAIR) return 'strong';

  if (ev.category === CAT_PAIR) {
    const pr = ev.ranks[0];
    if (pr > boardMaxRank) return 'strong';
    if (pr === boardMaxRank) return 'medium';
    if (pr >= boardMaxRank - 1 && boardMaxRank >= 10) return 'medium';
    return 'weak_showdown';
  }

  if (ev.category === CAT_HIGH) {
    if (ev.ranks[0] === 14) return 'weak_showdown';
    return 'air';
  }

  return 'air';
}

/**
 * @typedef {'none'|'strong'|'medium'|'weak'} DrawBucket
 */
export function classifyDraw(hole, board) {
  const h = pokerCardsOnly(hole || []);
  const bd = pokerCardsOnly(board || []);
  if (h.length < 2 || bd.length < 3) return 'none';

  const all = [...h, ...bd];
  for (let s = 0; s < 4; s += 1) {
    if (all.filter((c) => c.suit === s).length >= 4) {
      const hc = h.filter((c) => c.suit === s);
      if (hc.length >= 1) {
        const mx = Math.max(...hc.map((c) => c.rank));
        return mx >= 12 ? 'strong' : 'medium';
      }
    }
  }

  const R = new Set(all.map((c) => c.rank));
  for (let hi = 14; hi >= 5; hi -= 1) {
    let c = 0;
    for (let k = 0; k < 5; k += 1) {
      const v = hi - k;
      if (v < 2) break;
      if (R.has(v)) c += 1;
    }
    if (c >= 4) return 'medium';
  }
  if (R.has(14) && R.has(5) && R.has(4) && R.has(3) && R.has(2)) return 'medium';

  const sorted = [...R].sort((a, b) => a - b);
  for (let i = 0; i < sorted.length - 2; i += 1) {
    const span = sorted[i + 2] - sorted[i];
    if (span <= 4 && h.some((c) => c.rank >= sorted[i] && c.rank <= sorted[i] + 4)) return 'weak';
  }

  return 'none';
}

/**
 * @typedef {'dry'|'wet'|'paired'} BoardTexture
 */
export function classifyBoardTexture(board) {
  const b = pokerCardsOnly(board || []);
  if (b.length < 3) return 'dry';
  const ranks = b.map((c) => c.rank);
  if (new Set(ranks).size < ranks.length) return 'paired';

  const suits = [0, 1, 2, 3].map((s) => b.filter((c) => c.suit === s).length);
  const maxS = Math.max(...suits);
  const sortedR = [...new Set(ranks)].sort((a, b) => a - b);
  let connected = false;
  for (let i = 0; i < sortedR.length - 1; i += 1) {
    if (sortedR[i + 1] - sortedR[i] <= 2) connected = true;
  }

  if (maxS >= 2 && connected) return 'wet';
  if (sortedR.length >= 3 && sortedR[sortedR.length - 1] - sortedR[0] <= 4) return 'wet';
  return 'dry';
}

/**
 * @typedef {'early'|'middle'|'late'|'blinds'} PositionBucket
 */
export function getPositionBucket(dealerSeat, seat) {
  const d = ((seat - dealerSeat) % NUM_SEATS + NUM_SEATS) % NUM_SEATS;
  if (d === 1 || d === 2) return 'blinds';
  if (d === 0) return 'late';
  if (d === 5) return 'late';
  if (d === 4) return 'middle';
  return 'early';
}

export function boardTopRank(board) {
  const b = pokerCardsOnly(board || []);
  if (!b.length) return 0;
  return Math.max(...b.map((c) => c.rank));
}
